/**
 * Bingo Card List Component
 *
 * Displays a list of bingo cards with filtering options.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Filter } from 'lucide-react';
import type { BingoCard, BingoCardFilters } from '@/types/bingo';
import { useBingoCards } from '@/hooks/use-bingo';
import { Button, Card } from '@/components/ui';

interface BingoCardListProps {
  onCreateClick?: () => void;
}

export function BingoCardList({ onCreateClick }: BingoCardListProps) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<BingoCardFilters>({});
  const { data, isLoading, error } = useBingoCards(filters);

  const currentYear = new Date().getFullYear();

  const handleCardClick = (card: BingoCard) => {
    navigate(`/bingo/${card.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading bingo cards...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">Error loading bingo cards</div>
      </div>
    );
  }

  const cards = data?.cards || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Goal Bingo</h1>
        <Button onClick={onCreateClick} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Card
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <Filter className="w-4 h-4 text-gray-500" />
        <select
          value={filters.year || ''}
          onChange={(e) =>
            setFilters({ ...filters, year: e.target.value ? parseInt(e.target.value) : undefined })
          }
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="">All Years</option>
          <option value={currentYear}>{currentYear}</option>
          <option value={currentYear - 1}>{currentYear - 1}</option>
          <option value={currentYear + 1}>{currentYear + 1}</option>
        </select>
        <select
          value={filters.status || ''}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value as BingoCard['status'] | undefined })
          }
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Cards grid */}
      {cards.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No bingo cards yet</h3>
          <p className="text-gray-600 mb-4">Create your first goal bingo card to get started!</p>
          <Button onClick={onCreateClick}>Create Your First Card</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Card
              key={card.id}
              className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleCardClick(card)}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{card.title}</h3>
              <p className="text-sm text-gray-600 mb-4">Year {card.year}</p>
              <div className="flex items-center justify-between">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    card.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : card.status === 'completed'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {card.status}
                </span>
                <span className="text-sm text-gray-500">{card.gridSize}x{card.gridSize}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

